import { useEffect, useState } from 'react';

export interface KeyboardViewport {
  // Piksel yang ditutup keyboard di dasar layar (0 saat tertutup).
  inset: number;
  // Seberapa jauh Safari mem-pan visual viewport ke bawah (iOS). Elemen
  // fixed-top perlu translateY(offsetTop) agar tetap terlihat.
  offsetTop: number;
}

// iOS Safari TIDAK meresize layout viewport saat keyboard muncul. Dua gejala:
// (1) elemen fixed-bottom tertutup keyboard, (2) Safari mem-pan halaman untuk
// menampakkan input yang difokus, sehingga top bar "bablas" keluar layar dan
// tidak kembali. Hook ini memberi angka untuk melawan keduanya, plus dua
// side-effect standar selama keyboard terbuka: dokumen dikunci (overflow
// hidden) dan window scroll dipaksa kembali ke 0, supaya pan Safari tidak
// meninggalkan halaman dalam posisi setengah-tergeser.
// Android ditangani lebih hulu oleh viewport meta
// interactive-widget=resizes-content (inset di sini praktis 0).
export default function useKeyboardInset(enabled: boolean): KeyboardViewport {
  const [state, setState] = useState<KeyboardViewport>({ inset: 0, offsetTop: 0 });

  useEffect(() => {
    if (!enabled) {
      setState({ inset: 0, offsetTop: 0 });
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;

    const prevBodyOverflow = document.body.style.overflow;
    let locked = false;

    const update = () => {
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // <60px = bukan keyboard (URL bar collapse dsb).
      const keyboardOpen = covered > 60;
      const offsetTop = keyboardOpen ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      setState({ inset: keyboardOpen ? Math.round(covered) : 0, offsetTop });

      // TIDAK ada lagi scrollTo(0,0) di sini (2026-08-04): shell sekarang
      // MENGIKUTI pan Safari lewat --app-y (lihat Chat.tsx + useTheme
      // wrapper). Melawan pan sambil mengikutinya membuat dua mekanisme
      // berebut dan hasilnya intermiten. Kunci overflow body dipertahankan.
      if (keyboardOpen) {
        if (!locked) {
          document.body.style.overflow = 'hidden';
          locked = true;
        }
      } else if (locked) {
        document.body.style.overflow = prevBodyOverflow;
        locked = false;
      }
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      if (locked) document.body.style.overflow = prevBodyOverflow;
    };
  }, [enabled]);

  return state;
}
